import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { PullRegistrationDialog } from './components/PullRegistrationDialog';
import { EventDetailsHeader } from './components/EventDetailsHeader';
import { RegistrationsTable } from './components/RegistrationsTable';
import { useEventDetails } from './hooks/useEventDetails';
import { useRegistrations } from './hooks/useRegistrations';
import { useRetriggerRegistrations } from './hooks/useRetriggerRegistrations';
import type { RegistrationRow, RetriggerRegistrationRequest } from './types';

function getRegistrationCode(row: RegistrationRow): string | null {
  const code = row.registrationCode?.trim();
  return code ? code : null;
}

export function EventDetailsPage() {
  const { eventCode, accountCode } = useParams<{ eventCode: string; accountCode: string }>();
  const [selectedRegistrationCodes, setSelectedRegistrationCodes] = useState<Set<string>>(
    () => new Set(),
  );
  const [isPullDialogOpen, setPullDialogOpen] = useState(false);
  const detailsQuery = useEventDetails(eventCode, accountCode);
  const registrationsQuery = useRegistrations(eventCode, accountCode);
  const retriggerMutation = useRetriggerRegistrations({ eventCode, accountCode });

  const registrations = useMemo(() => registrationsQuery.data ?? [], [registrationsQuery.data]);

  useEffect(() => {
    if (!registrationsQuery.data) return;
    const currentRegistrationCodes = new Set(
      registrationsQuery.data
        .map(getRegistrationCode)
        .filter((code): code is string => Boolean(code)),
    );
    setSelectedRegistrationCodes((current) => {
      const next = new Set<string>();
      current.forEach((code) => {
        if (currentRegistrationCodes.has(code)) next.add(code);
      });
      return next;
    });
  }, [registrationsQuery.data]);

  function toggleRegistration(registrationCode: string, selected: boolean) {
    setSelectedRegistrationCodes((current) => {
      const next = new Set(current);
      if (selected) next.add(registrationCode);
      else next.delete(registrationCode);
      return next;
    });
  }

  function toggleAllRegistrations(registrationCodes: string[], selected: boolean) {
    setSelectedRegistrationCodes((current) => {
      const next = new Set(current);
      registrationCodes.forEach((registrationCode) => {
        if (selected) next.add(registrationCode);
        else next.delete(registrationCode);
      });
      return next;
    });
  }

  function buildRequestsFromSelected(): RetriggerRegistrationRequest[] {
    if (!eventCode || !accountCode) return [];
    return registrations.flatMap((registration) => {
      const registrationCode = getRegistrationCode(registration);
      if (!registrationCode || !selectedRegistrationCodes.has(registrationCode)) return [];
      return [
        {
          eventCode: registration.eventCode || eventCode,
          accountCode: registration.accountCode || accountCode,
          registrationCode,
        },
      ];
    });
  }

  async function handleReprocessSelected() {
    const request = buildRequestsFromSelected();
    if (request.length === 0) {
      toast.error('No user selected!');
      return;
    }

    try {
      await retriggerMutation.mutateAsync(request);
      setSelectedRegistrationCodes(new Set());
      toast.success('Success!');
    } catch {
      toast.error('API failure!');
    }
  }

  async function handlePullRegistration(registrationCodes: string[]) {
    if (!eventCode || !accountCode) return;

    const request = registrationCodes.map((registrationCode) => ({
      eventCode,
      accountCode,
      registrationCode,
    }));

    try {
      await retriggerMutation.mutateAsync(request);
      setPullDialogOpen(false);
      toast.success('Success!');
    } catch {
      toast.error('API failure!');
    }
  }

  return (
    <section className="space-y-5" aria-labelledby="event-details-heading">
      {!eventCode || !accountCode ? (
        <div role="alert" className="border border-dds-red bg-red-50 p-3 text-sm text-red-700">
          Invalid event details URL.
        </div>
      ) : (
        <>
          <EventDetailsHeader
            details={detailsQuery.data}
            eventCode={eventCode}
            accountCode={accountCode}
            isLoading={detailsQuery.isLoading}
            isError={detailsQuery.isError}
          />

          <RegistrationsTable
            registrations={registrations}
            selectedRegistrationCodes={selectedRegistrationCodes}
            isLoading={registrationsQuery.isLoading}
            isError={registrationsQuery.isError}
            isReprocessing={retriggerMutation.isPending}
            onToggleRegistration={toggleRegistration}
            onToggleAllRegistrations={toggleAllRegistrations}
            onOpenPullRegistrations={() => setPullDialogOpen(true)}
            onReprocessSelected={() => void handleReprocessSelected()}
          />

          <PullRegistrationDialog
            open={isPullDialogOpen}
            eventCode={eventCode}
            accountCode={accountCode}
            isSubmitting={retriggerMutation.isPending}
            onClose={() => setPullDialogOpen(false)}
            onSubmit={handlePullRegistration}
          />
        </>
      )}
    </section>
  );
}
